/**
 * Pure logic for the multi-location experience layer. Kept free of React so the
 * seeding + gating rules are unit-testable in isolation (CLAUDE.md: core logic
 * lives in testable helpers, not inside the component).
 *
 * Approved architecture (2026-06-22): COMPANY auto-resolved · LOCATION is the
 * only layer users navigate (default home shop, reach-across) · ROLE is the
 * world. Techs are locked to their bay and get no switcher.
 */

/** Roles allowed to switch shops. A technician has no switcher (locked to bay). */
export const SWITCHER_ROLES = ['manager', 'admin', 'leadership'] as const;
export type SwitcherRole = (typeof SWITCHER_ROLES)[number];

export function canSwitchLocations(role: string | null | undefined): boolean {
  return !!role && (SWITCHER_ROLES as readonly string[]).includes(role);
}

/**
 * Resolve the initial selected shop for a signed-in user.
 *  - A previously stored choice always wins. The stored value `''` encodes
 *    "All Shops" (company-wide → null); any other non-null string is a shop id.
 *  - Else a manager defaults to their HOME shop (reach-across model — they can
 *    still reach the other shops via the switcher).
 *  - Else (admin / leadership, or a manager with no home shop) → All Shops.
 */
export function resolveInitialLocation(opts: {
  role: string | null | undefined;
  primaryLocationId: string | null;
  /** Raw localStorage value, or null when nothing was stored yet. */
  stored: string | null;
}): string | null {
  const { role, primaryLocationId, stored } = opts;
  if (stored !== null) return stored === '' ? null : stored;
  if (role === 'manager' && primaryLocationId) return primaryLocationId;
  return null;
}
